import type { UseFormSetError, FieldValues, Path } from 'react-hook-form'

interface ServerFieldError {
  field: string
  message: string
}

interface ServerErrorResponse {
  error?: {
    code?: string
    message?: string
    details?: ServerFieldError[] | Record<string, string>
  }
}

// Keys that identify a backend ERROR ENVELOPE ({ code, message, statusCode,
// timestamp, requestId, path }) rather than a field→message map. If any of
// these appears among the object keys, the object is NOT a field map.
const ERROR_ENVELOPE_KEYS: ReadonlySet<string> = new Set([
  'code',
  'message',
  'statusCode',
  'timestamp',
  'requestId',
  'path',
])

/**
 * Maps server-side validation errors to react-hook-form field errors.
 *
 * Supports two server error formats:
 *   - Array:  [{ field: 'email', message: 'Already exists' }]
 *   - Object: { email: 'Already exists', password: 'Too short' }
 *
 * Returns true if at least one field error was mapped, false otherwise.
 * When false the caller should fall back to displaying a generic error banner.
 *
 * Usage:
 *   const mapped = mapServerErrorsToForm(serverError, setError);
 *   if (!mapped) setGlobalError(serverError.error?.message ?? 'Unknown error');
 */
export function mapServerErrorsToForm<T extends FieldValues>(
  serverError: ServerErrorResponse,
  setError: UseFormSetError<T>,
): boolean {
  const details = serverError.error?.details

  if (!details) return false

  let mapped = false

  if (Array.isArray(details)) {
    for (const { field, message } of details) {
      setError(field as Path<T>, { type: 'server', message })
      mapped = true
    }
  } else if (typeof details === 'object') {
    // HARDENED (login error-envelope bug): api.service used to fall back to
    // `details: <full error envelope>` ({ code, message, ... }). Treating ANY
    // object as a field→message map made setError() fire on non-existent
    // fields ("code", "message"), report mapped=true, and suppress the global
    // error banner. Only treat the object as a field map when EVERY value is
    // a string AND NO key is an envelope key; otherwise refuse to map so the
    // caller falls back to the visible generic banner.
    const entries = Object.entries(details as Record<string, unknown>)
    const isFieldToMessageMap =
      entries.length > 0 &&
      entries.every(
        ([field, message]) => typeof message === 'string' && !ERROR_ENVELOPE_KEYS.has(field),
      )
    if (!isFieldToMessageMap) return false

    for (const [field, message] of entries) {
      setError(field as Path<T>, { type: 'server', message: String(message) })
      mapped = true
    }
  }

  return mapped
}
